import { Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';
import {
  API_DELETE,
  API_PATCH,
  API_POST,
  DBRequest,
  useConfig,
} from '../../../../api/api';
import useFetch from '../../../../api/useFetch';
import Feedback from '../../../special/Feedback';
import Pagination from '../../../special/Pagination';
import { updateOrCreate } from '../../../special/updateOrCreate';
import ExoAlert from '../../ExoAlert';
import { ExoSkeleton } from '../../ExoSkeleton';
import NewWPEToDoElement from '../work-package-elements/NewWPEToDoElement';
import WPEToDoElementPopup from '../work-package-elements/WPEToDoElementPopup';
import TaskCard from '../task-card/TaskCard';
import TaskCardList from '../task-card/TaskCardList';
import { buildTaskFilterQuery, sortTasks } from './taskFilterQuery';
import { taskStatsDelta } from './overview/overviewStats';

/**
 * Tasks of a single work package.
 * Mounted by the work package collapse, so the request runs on expand and the
 * data lives exactly as long as the package stays open.
 * Sorting and user filters (priority, effortScore) are supported by the API
 * and get appended to the path once they are wired up.
 */
const WorkPackageTaskList = ({
  project,
  workPackage,
  filter,
  onLoadingChange,
  onStatsChange,
  // the pool is a place to pick work up, not to create it
  allowCreate = true,
}) => {
  const { t } = useTranslation();
  const config = useConfig();

  // report what a changed task means for the counters of the overview
  function reportStats(before, after) {
    if (onStatsChange)
      onStatsChange(workPackage.id, taskStatsDelta(before, after));
  }

  const filterQuery = buildTaskFilterQuery(filter);

  // changing a filter changes the path, which refetches the list
  const { apiData, setApiData, reloadApiData, isLoading, setIsLoading } =
    useFetch(`work-packages/${workPackage.id}/tasks${filterQuery}`);

  const paginationData = apiData?.meta?.links
    ? {
        ...apiData,
        meta: {
          ...apiData.meta,
          links: apiData.meta.links.map((link) => ({
            ...link,
            url: link.url ? `${link.url}&${filterQuery.slice(1)}` : null,
          })),
        },
      }
    : apiData;

  // the first load has its own skeleton, a refetch is reported to the view so
  // it can show one processing indicator for all open lists
  const isRefetching = isLoading && Boolean(apiData);

  useEffect(() => {
    if (!onLoadingChange) return;
    onLoadingChange(workPackage.id, isRefetching);
    return () => onLoadingChange(workPackage.id, false);
  }, [isRefetching]);

  const [isCreating, setIsCreating] = useState(false);
  const [processingIds, setProcessingIds] = useState([]);
  const [assigningIds, setAssigningIds] = useState([]);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [taskToDelete, setTaskToDelete] = useState(null);

  const userId = Number(new Cookies().get('userId'));

  // only a member of the work package may take a task over
  const isWorkPackageMember = (workPackage.users || []).some(
    (user) => Number(user.id) === userId,
  );

  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: '',
  });

  // DBRequest reports the API message, which is empty on some endpoints
  function handleFeedback(text, type, fallbackText) {
    setAlertState({
      alertOpen: true,
      alertType: type,
      alertText: text || fallbackText,
    });
  }

  function setProcessing(taskId, isProcessing) {
    setProcessingIds((prev) =>
      isProcessing ? [...prev, taskId] : prev.filter((id) => id !== taskId),
    );
  }

  // assign the current user to a task
  function handleAssignSelf(task) {
    if (!userId || assigningIds.includes(task.id)) return;

    DBRequest({
      config,
      path: `tasks/${task.id}/members`,
      method: API_POST({
        relations: [userId],
        mode: 'sync',
        relationType: 'users',
      }),
      onLoading: (isAssigning) =>
        setAssigningIds((prev) =>
          isAssigning
            ? [...prev, task.id]
            : prev.filter((id) => id !== task.id),
        ),
      // the response carries the updated task, so no reload is needed
      onResponse: (res) => setApiData((prev) => updateOrCreate(prev, res.data)),
      onFeedback: (text, type) =>
        handleFeedback(text, type, t('Task assigned to you')),
    });
  }

  // delete todo
  function handleDelete() {
    const task = taskToDelete;

    DBRequest({
      config,
      path: `work-packages/elements/list/tasks/${task.id}`,
      method: API_DELETE(),
      onResponse: () => {
        setApiData((prev) => ({
          ...prev,
          data: prev.data.filter((obj) => obj.id !== task.id),
        }));
        setTaskToDelete(null);
        reportStats(task, null);
      },
      onFeedback: (text, type) => handleFeedback(text, type, t('Task deleted')),
    });
  }

  // add a link to a task, leaving its other fields untouched
  function handleLinkAdd(task, link) {
    DBRequest({
      config,
      path: `work-packages/elements/list/tasks/${task.id}`,
      method: API_PATCH({
        externalResources: [...(task.externalResources || []), link],
      }),
      onResponse: (res) => setApiData((prev) => updateOrCreate(prev, res.data)),
      onFeedback: (text, type) => handleFeedback(text, type, t('Task updated')),
    });
  }

  // the explorer returns the complete file selection of the todo
  function handleFilesChange(task, files) {
    DBRequest({
      config,
      path: `work-packages/elements/list/tasks/${task.id}`,
      method: API_PATCH({ ...task, files }),
      onResponse: (res) => setApiData((prev) => updateOrCreate(prev, res.data)),
      onFeedback: (text, type) => handleFeedback(text, type, t('Task updated')),
    });
  }

  // remove one link, sending the todo with the remaining ones
  function handleLinkDelete(task, resource) {
    DBRequest({
      config,
      path: `work-packages/elements/list/tasks/${task.id}`,
      method: API_PATCH({
        ...task,
        externalResources: (task.externalResources || []).filter(
          (obj) => obj.id !== resource.id,
        ),
      }),
      onResponse: (res) => setApiData((prev) => updateOrCreate(prev, res.data)),
      onFeedback: (text, type) => handleFeedback(text, type, t('Task updated')),
    });
  }

  // a comment becomes an entry of the task history
  function handleCommentSubmit(task, text) {
    DBRequest({
      config,
      path: `tasks/${task.id}/history`,
      method: API_POST({ text }),
      onResponse: (res) =>
        setApiData((prev) =>
          updateOrCreate(prev, {
            ...task,
            history: [res.data, ...(task.history || [])],
          }),
        ),
      onFeedback: (message, type) =>
        handleFeedback(message, type, t('Comment added')),
    });
  }

  // the new task comes back complete, so it is put into the list directly
  function handleCreate(res) {
    const task = res?.data || res;

    setApiData((prev) => updateOrCreate(prev, task));
    setIsCreating(false);
    reportStats(null, task);
    handleFeedback(null, 'success', t('Task created'));
  }

  // toggle a task's status between open and done
  function handleToggleComplete(task) {
    if (processingIds.includes(task.id)) return;

    DBRequest({
      config,
      path: `work-packages/elements/list/tasks/${task.id}`,
      method: API_PATCH({ status: task.status ? 0 : 1 }),
      onLoading: (isProcessing) => setProcessing(task.id, isProcessing),
      onResponse: (res) => {
        setApiData((prev) => updateOrCreate(prev, res.data));
        reportStats(task, res.data);
      },
      onFeedback: (text, type) => handleFeedback(text, type, t('Task updated')),
    });
  }

  // only the first load shows the skeleton - a reload keeps the list visible
  if (!apiData)
    return (
      <Box className="flex flex-col gap-2">
        <ExoSkeleton n={3} size="70px" />
      </Box>
    );

  const tasks = sortTasks(apiData.data || [], filter?.sortBy);

  // tasks belong to the todo list element of the work package - without one
  // there is nothing to add them to
  const listElementId =
    workPackage.elements?.find((element) => element.type === 'list')?.id ||
    tasks[0]?.workPackageListElement?.id;

  return (
    <Box className="flex flex-col gap-2">
      {/* an empty page keeps the pagination, so page 1 stays reachable */}
      {!tasks.length && !isCreating && (
        <Typography fontSize="13px" className="opacity-50 text-center py-4">
          {t('No data available')}
        </Typography>
      )}

      {/* enter or a click outside creates the task, escape cancels it */}
      {isCreating && (
        <NewWPEToDoElement
          wpeId={listElementId}
          onSubmit={handleCreate}
          onCancel={() => setIsCreating(false)}
        />
      )}

      {allowCreate && Boolean(listElementId) && !isCreating && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setIsCreating(true)}
          sx={{ alignSelf: 'flex-start', fontSize: '12px' }}
        >
          {t('Add Task')}
        </Button>
      )}

      <TaskCardList>
        {tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            workPackage={workPackage}
            isProcessing={processingIds.includes(task.id)}
            onToggleComplete={handleToggleComplete}
            isAssigning={assigningIds.includes(task.id)}
            onAssignSelf={isWorkPackageMember ? handleAssignSelf : null}
            editmode={Boolean(project?.editable)}
            projectId={project?.id}
            // the members response has no pictures, so the list is reloaded
            onUsersChange={reloadApiData}
            onFilesChange={handleFilesChange}
            onLinkAdd={handleLinkAdd}
            onLinkDelete={handleLinkDelete}
            onCommentSubmit={handleCommentSubmit}
            onEdit={setTaskToEdit}
            onDelete={setTaskToDelete}
          />
        ))}
      </TaskCardList>

      {apiData?.meta && (
        <Pagination
          data={paginationData}
          setData={setApiData}
          setLoading={setIsLoading}
          isSmall
        />
      )}

      {/* the dialog the work package view edits a task with */}
      {taskToEdit && (
        <WPEToDoElementPopup
          open={Boolean(taskToEdit)}
          element={taskToEdit}
          pid={project?.id}
          wpeId={taskToEdit.workPackageListElement?.id}
          onClose={() => setTaskToEdit(null)}
          onResponse={(res) => {
            // editing can change the deadline and the points of a task
            reportStats(taskToEdit, res?.data || res);
            setTaskToEdit(null);
            reloadApiData();
          }}
          onDelete={() => {
            setTaskToEdit(null);
            setTaskToDelete(taskToEdit);
          }}
        />
      )}

      <ExoAlert
        open={Boolean(taskToDelete)}
        onClose={() => setTaskToDelete(null)}
        onSubmit={handleDelete}
        alert={{
          submitLabel: t('Delete'),
          submitColor: 'error',
          message: t('Are you sure you want to permanently delete this entry?'),
        }}
      />
      {/* the cards above sit inside backdrop-blurred boxes, which would make
          the fixed snackbar stick to that box instead of the viewport */}
      {createPortal(
        <Feedback state={alertState} setState={setAlertState} />,
        document.body,
      )}
    </Box>
  );
};

export default WorkPackageTaskList;
